import { enCatalog } from '../i18n/catalog/en';
import { isInScope, sectionForKey, TRANSLATION_SECTIONS, OTHER_SECTION, type TranslationSection } from '../i18n/sections';
import { sourceHash } from '../i18n/sourceHash';
import { BUILT_IN_CATALOGS } from '../i18n/translate';
import type { ServerTranslation, TranslationStatus } from '../services/i18nService';

/** What a translator needs to do with a string. */
export type RowState = 'missing' | 'stale' | 'ai_draft' | 'draft' | 'approved';

export interface TranslationRow {
  key: string;
  sectionId: string;
  english: string;
  englishHash: string;
  text: string;
  /** Null when nothing is saved yet (text, if any, is the built-in AI draft). */
  serverStatus: TranslationStatus | null;
  state: RowState;
  updatedBy?: string;
  updatedAt?: string;
  approvedBy?: string;
  approvedAt?: string;
}

export interface RowCounts {
  total: number;
  missing: number;
  stale: number;
  ai_draft: number;
  draft: number;
  approved: number;
}

export const ROW_STATE_LABELS: Record<RowState, string> = {
  missing: 'Missing',
  stale: 'English changed',
  ai_draft: 'AI draft',
  draft: 'Edited — not approved',
  approved: 'Approved',
};

/**
 * One row per English key. A saved translation wins; otherwise the built-in
 * catalog's text is shown as an AI draft (it ships with the app, nothing to import).
 */
export function buildRows(server: ServerTranslation[], code: string): TranslationRow[] {
  const byKey = new Map(server.map((s) => [s.key, s]));
  const builtIn = BUILT_IN_CATALOGS[code] || {};
  return Object.entries(enCatalog)
    .filter(([key]) => isInScope(key))
    .map(([key, english]) => {
    const englishHash = sourceHash(english);
    const saved = byKey.get(key);
    const savedText = saved?.text ?? '';
    const builtInText = builtIn[key] ?? '';
    let text: string;
    let state: RowState;
    if (saved && savedText.trim()) {
      text = savedText;
      state = saved.source_hash !== englishHash ? 'stale' : saved.status;
    } else if (builtInText.trim()) {
      text = builtInText;
      state = 'ai_draft';
    } else {
      text = '';
      state = 'missing';
    }
    return {
      key,
      sectionId: sectionForKey(key).id,
      english,
      englishHash,
      text,
      serverStatus: saved?.status ?? null,
      state,
      updatedBy: saved?.updated_by,
      updatedAt: saved?.updated_at,
      approvedBy: saved?.approved_by,
      approvedAt: saved?.approved_at,
    };
  });
}

export function countRows(rows: TranslationRow[]): RowCounts {
  const counts: RowCounts = { total: rows.length, missing: 0, stale: 0, ai_draft: 0, draft: 0, approved: 0 };
  for (const r of rows) counts[r.state] += 1;
  return counts;
}

/** Strings that still need a translator before the language can be marked ready. */
export function needsAttention(counts: RowCounts): number {
  return counts.total - counts.approved;
}

export function percentApproved(counts: RowCounts): number {
  if (counts.total === 0) return 100;
  return Math.floor((counts.approved / counts.total) * 100);
}

export interface SectionSummary {
  section: TranslationSection;
  counts: RowCounts;
}

export function summarizeSections(rows: TranslationRow[]): SectionSummary[] {
  const all = [...TRANSLATION_SECTIONS, OTHER_SECTION];
  return all
    .map((section) => ({
      section,
      counts: countRows(rows.filter((r) => r.sectionId === section.id)),
    }))
    .filter((s) => s.counts.total > 0);
}

const PLACEHOLDER_RE = /\{[a-zA-Z0-9_]+\}/g;

/** Placeholders like {count} present in English but missing from the translation. */
export function missingPlaceholders(english: string, translation: string): string[] {
  const needed = Array.from(new Set(english.match(PLACEHOLDER_RE) ?? []));
  return needed.filter((p) => !translation.includes(p));
}
